import { useState, useEffect, useRef } from 'react';
import { postData, getData } from '../../ApiServices/ApiService';
import { ORG_EXPENSE_LIST_REPORT, ORG_GET_EMPLOYEE_LIST } from '../../ApiServices/BaseURL';
import { logFrontendException } from '../../Utils/Exception';
import { useTheme } from '../../GlobalContext/GlobalContext';

const AllStatusListController = (actionType, selectedYear) => {
    const [expenses, setExpenses] = useState([]);
    const [filters, setFilters] = useState({
        approved_date: '',
        expense_date: '',
        amount: '',
        approved_by: '',
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [showDisModal, setShowDisModal] = useState(false);
    const expenseInfoRef = useRef(null);

    const itemsPerPage = 10;
    const totalPages = Math.ceil(expenses.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentExpenses = expenses.slice(indexOfFirstItem, indexOfLastItem);

    const [statusListError, setStatusListError] = useState("");

    const [userLoading, setUserLoading] = useState(false);
    const [userError, setUserError] = useState('');
    const [userOptions, setUserOptions] = useState([]);
    const { setIsLoginPopup } = useTheme();
    const pageName = 'AllStatusListController';
    const storedUserId = JSON.parse(localStorage.getItem("_userInfo_"))?.emp_id;

    const toggleFilters = () => setShowFilters(!showFilters);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const resetFilters = () => {
        if (filters?.approved_date || filters?.expense_date || filters?.amount || filters?.approved_by) {
            fetchStatusList();
            setCurrentPage(1);
        }
        setFilters({
            approved_date: '',
            expense_date: '',
            amount: '',
            approved_by: '',
        });
        setError('');
    };

    const cancelFilters = () => {
        resetFilters();
        setShowFilters(false);
    };

    const applyFilters = async () => {
        setLoading(true);
        setStatusListError("");
        setError('');
        setCurrentPage(1);

        const storedUserInfo = JSON.parse(localStorage.getItem("_userInfo_"));

        const { approved_date, expense_date, amount, approved_by } = filters;

        if (!approved_date && !expense_date && !amount && !approved_by) {
            setLoading(false);
            setError("Please fill at least one field to search.");
            return;
        }
        setExpenses([]);

        try {
            const payload = {
                emp_id_auth: storedUserInfo?.emp_id,
                action_type: actionType,
                filters: filters
            };

            console.log("applyFilters payload", payload);

            const response = await postData(ORG_EXPENSE_LIST_REPORT, payload);
            const responseData = await response.json();

            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message?.toLowerCase() === "success") {
                        if (responseData?.dataJ?.length > 0) {
                            setExpenses(responseData?.dataJ);
                        } else {
                            setExpenses([]);
                            setStatusListError(responseData?.info || "No records found.");
                            logFrontendException(storedUserId, pageName, "applyFilters", responseData?.info || '');
                        }
                    } else {
                        setStatusListError(responseData?.info || "Failed to fetch filter list.");
                        logFrontendException(storedUserId, pageName, "applyFilters:message", responseData?.info || '');
                    }
                } else {
                    setStatusListError(responseData?.info || "Failed to fetch filter list.Please try again later.");
                    logFrontendException(storedUserId, pageName, "applyFilters:statusCode", responseData || '');
                }
            } else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                setStatusListError('Unauthorized access. Your session may have expired. Please log in again.');
                logFrontendException(storedUserId, pageName, "applyFilters", responseData || '');
                setIsLoginPopup(true);
            } else {
                setStatusListError("Error While fetching filter list.");
                logFrontendException(storedUserId, pageName, "applyFilters:api_failed", response || '');
            }
        } catch (error) {
            setStatusListError("Failed to fetch filter list.Please check your network connection and try again.");
            logFrontendException(storedUserId, pageName, "applyFilters:exception", error || '');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const maxVisiblePages = 3;
    const getVisiblePages = () => {
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        const adjustedStart = Math.max(1, endPage - maxVisiblePages + 1);
        return Array.from({ length: endPage - adjustedStart + 1 }, (_, idx) => adjustedStart + idx);
    };

    const fetchStatusList = async () => {
        setLoading(true);
        setStatusListError("");
        setExpenses([]);
        const storedUserInfo = JSON.parse(localStorage.getItem("_userInfo_"));

        try {
            const payload = {
                emp_id_auth: storedUserInfo?.emp_id,
                action_type: actionType,
                fy: selectedYear.value,
            };

            console.log("payload", payload);

            const response = await postData(ORG_EXPENSE_LIST_REPORT, payload);
            const responseData = await response.json();

            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message?.toLowerCase() === "success") {
                        if (responseData?.dataJ?.length > 0) {
                            setExpenses(responseData?.dataJ);
                        } else {
                            setExpenses([]);
                            setStatusListError(responseData?.info || "No records found.");
                            logFrontendException(storedUserId, pageName, "fetchStatusList", responseData?.info || '');
                        }
                    } else {
                        setStatusListError(responseData?.info || "Failed to fetch list.");
                        logFrontendException(storedUserId, pageName, "fetchStatusList:message", responseData?.info || '');
                    }
                } else {
                    setStatusListError(responseData?.info || "Failed to fetch list.Please try again.");
                    logFrontendException(storedUserId, pageName, "fetchStatusList:statusCode", responseData || '');
                }
            } else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                setStatusListError('Unauthorized access. Your session may have expired. Please log in again.');
                logFrontendException(storedUserId, pageName, "fetchStatusList", responseData || '');
                setIsLoginPopup(true);
            } else {
                setStatusListError("Error occurred while fetching list.");
                logFrontendException(storedUserId, pageName, "fetchStatusList:api_failed", response || '');
            }
        } catch (error) {
            setStatusListError("Failed to fetch list.Please check your network connection and try again.");
            logFrontendException(storedUserId, pageName, "fetchStatusListCatch", error || '');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (actionType) {
            fetchStatusList();
        }
    }, [actionType]);

    useEffect(() => {
        if (actionType !== "pending_expenses") {
            fetchUserList();
        }
    }, [actionType]);

    const visiblePages = getVisiblePages();

    const openModal = () => {
        expenseInfoRef.current = null;
        setShowModal(true);
        setShowDisModal(true);
    }

    const handleViewService = (expenseData) => {
        console.log("expenseData", expenseData)
        expenseInfoRef.current = {
            ...expenseData,
            pageType: 'view'
        };
        setShowModal(true);
    }

    const handleApproveReject = (expenseData) => {
        // TODO: Trigger modal or approval/rejection logic
        expenseInfoRef.current = {
            ...expenseData,
            pageType: 'approve'
        };
        setShowModal(true);
        console.log("Handle Approve/Reject", expenseData);
    };

    const handleDisApproveReject = (expenseData) => {
        setShowDisModal(true);
        expenseInfoRef.current = {
            ...expenseData,
            pageType: 'approve'
        };
        console.log("Handle Approve/Reject", expenseData);
    };


    const fetchUserList = async () => {
        try {
            setUserLoading(true);
            setUserError('');

            const storedUserInfo = JSON.parse(localStorage.getItem('_userInfo_'));

            // Make the API call
            const response = await getData(`${ORG_GET_EMPLOYEE_LIST}?emp_id_auth=${storedUserInfo?.emp_id}`);
            const responseData = await response.json();

            // Check for success (200) and internal status code
            if (response?.status === 200) {
                if (responseData?.statusCode === 200) {
                    if (responseData?.message.toLowerCase() === 'success') {
                        if (responseData?.dataJ?.length > 0) {
                            const formattedTeams = responseData.dataJ.map(service => ({
                                label: service.emp_name,
                                value: service.emp_id
                            }));
                            setUserOptions(formattedTeams);
                        } else {
                            setUserError(responseData?.info || 'Data Not Found');
                            logFrontendException(storedUserId, pageName, "fetchUserList", responseData?.info || '');
                        }
                    } else {
                        setUserError(responseData?.info || 'Failed to fetch User data. Please try again.');
                        logFrontendException(storedUserId, pageName, "fetchUserList", responseData?.info || '');
                    }
                } else {
                    setUserError(responseData?.info || 'Failed to fetch User data. Status code error.');
                    logFrontendException(storedUserId, pageName, "fetchUserList", responseData || '');
                }
            }
            // Token-related errors: 400, 401, 403
            else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                setUserError('Unauthorized access. Your session may have expired. Please log in again.');
                logFrontendException(storedUserId, pageName, "fetchUserList", responseData || '');
                setIsLoginPopup(true)
            }
            else {
                setUserError('Unexpected error occurred. Please try again later.');
                logFrontendException(storedUserId, pageName, "fetchUserList", response || '');
            }
        } catch (error) {
            setUserError(`Failed to fetch User data. Please check your network connection and try again.`);
            logFrontendException(storedUserId, pageName, "fetchUserListCatch", error || '');
        } finally {
            setUserLoading(false);
        }
    }

    return {
        expenses: currentExpenses,
        filters,
        showFilters,
        toggleFilters,
        handleFilterChange,
        resetFilters,
        cancelFilters,
        applyFilters,
        loading,
        error,
        currentPage,
        totalPages,
        handlePageChange,
        visiblePages,
        statusListError,
        showModal,
        closeModal: () => { setShowModal(false); setShowDisModal(false) },
        openModal,
        handleViewService,
        expenseInfoRef,
        handleApproveReject,
        userLoading,
        userError,
        userOptions,
        handleDisApproveReject,
        showDisModal,
        fetchStatusList,
    };
};

export default AllStatusListController;
